import Keycloak from 'keycloak-js';
import { checkLastVisitedOnLogin, storeLastVistiedOnLogout } from '../startupUrlCheck.js';
import dayjs from './dayjs.js';
import ApplicationLauncher from './applicationLauncher.js';
import CookieService from './cookieService.js';
import SystemDialogService from './systemDialogService.js';
import { Roles } from '../../../domain/konstanten/Roles.js';

const cookieService = new CookieService();
// Als "App" gilt nur das erste Pfad-Segment, damit z.B. /ma-app/zeiterfassung als ma-app gilt und nicht als zeiterfassung.
const isApp = (app) => new URL(window.location.href).pathname.split('/')[1] === app;
window.myVars = window.myVars || {};
window.myHandlers = window.myHandlers || {};
window.myHandlers.acceptAGB = acceptAGB;
window.myHandlers.denyAGB = denyAGB;
window.myVars.updateTokenTimeout = null;

/**
 * Initialisiert den Keycloak Service für das Session Management
 * - der Keycloak code wird im HTML Head geladen (../../dist/main.html Zeile 9)
 */
async function initKeycloak() {
    const response = await fetch('/assets/keycloak-config.json');
    const config = await response.json();
    const keycloak = new Keycloak(config);

    /**
     * Loggt den User aus. Als redirectUri geben wir bewusst nur origin + pathname mit:
     * die aktuelle URL kann unzulässige Query Parameter enthalten (z.B. /neolohn/?iss=blabla),
     * die Keycloak beim Logout mit "invalid redirect uri" ablehnen würde.
     */
    const logout = () => keycloak.logout({ redirectUri: `${window.location.origin}${window.location.pathname}` });

    // force update Token when online again
    window.addEventListener('online', () => {
        updateToken();
    });
    // force update Token when opening app again
    document.addEventListener('visibilitychange', function() {
        if (document.visibilityState === 'visible') {
           updateToken();
        }
      });

    keycloak.onAuthSuccess = async () => {
        cookieService.setCookie('access_token', keycloak.token);
        updateToken();
        await checkUserRoles();
        ApplicationLauncher.instance.setReady();
        await checkNeolohnAGB(keycloak.tokenParsed);

        checkLastVisitedOnLogin();
    };

    async function checkUserRoles() {
        // check if roles exist
        if (!keycloak.tokenParsed.resource_access?.neolohn?.roles) {
            return invalidUserLogout();
        }
        const roles = keycloak.tokenParsed.resource_access.neolohn.roles;
        window.myVars.UserRoles = roles;
        window.myVars.Username = keycloak.tokenParsed.name;
        window.myVars.UserBereiche = keycloak.tokenParsed?.bereichId || [];
        window.myVars.UserBetrieb = keycloak.tokenParsed?.betriebId || '';
        window.myVars.UserNeolohnId = keycloak.tokenParsed?.neolohnId ? keycloak.tokenParsed?.neolohnId : null;
        window.myVars.UserId = keycloak.tokenParsed.sub;

        const has = (role) => roles.includes(role);
        const redirect = (target) => { window.location.href = `${window.location.origin}${target}`; };

        // Falls nur eine Rolle vorhanden ist, leiten wir den User direkt zur passenden App weiter
        if (roles.length === 1) {
            if (has(Roles.Mitarbeiter) && !isApp('ma-app')) return redirect('/ma-app');
            if (has(Roles.Zeiterfassung) && !isApp('zeiterfassung')) return redirect('/zeiterfassung');
            if (has(Roles.Dienstplaner) && !isApp('dienstplan')) return redirect('/dienstplan');
        }

        // Falls der Benutzer kein Mitarbeiter ist, müssen wir ihn aus der Mitarbeiter App ausloggen...
        if (isApp('ma-app') && !has(Roles.Mitarbeiter)) {
            // Wenn der Benutzer neolohn Zugang hat, dann auf neolohn weiterleiten
            if (has(Roles.Sachbearbeiter) || has(Roles.DienstplanerMitBudget) || has(Roles.DienstplanerOhneBudget) || has(Roles.Zeitenpfleger)) {
                return redirect('/neolohn');
            }
            // Wenn der Benutzer Dienstplan Zugang hat, dann auf Dienstplan weiterleiten
            if (has(Roles.Dienstplaner)) {
                return redirect('/dienstplan');
            }
            return invalidUserLogout();
        }
        // Falls der Benutzer kein Sachbearbeiter oder Betriebsleiter User ist, müssen wir ihn aus neolohn ausloggen...
        if (isApp('neolohn')
            && !has(Roles.Sachbearbeiter)
            && !has(Roles.DienstplanerOhneBudget)
            && !has(Roles.DienstplanerMitBudget)
            && !has(Roles.Zeitenpfleger)) {
            // Falls der User Dienstplan hat, dann auf Dienstplan gehen
            if (has(Roles.Dienstplaner)) {
                return redirect('/dienstplan');
            }
            return invalidUserLogout();
        }
        // Falls der Benutzer keine Dienstplan-Berechtigung hat, müssen wir ihn aus dem Dienstplan ausloggen...
        if (isApp('dienstplan')
            && !has(Roles.Sachbearbeiter)
            && !has(Roles.Dienstplaner)
            && !has(Roles.DienstplanerOhneBudget)
            && !has(Roles.DienstplanerMitBudget)
            && !has(Roles.Zeitenpfleger)) {
            // Wenn der Benutzer Mitarbeiter ist, dann auf die Mitarbeiter App weiterleiten
            if (has(Roles.Mitarbeiter)) {
                return redirect('/ma-app');
            }
            // Wenn der Benutzer Zeiterfassung ist, dann auf die Zeiterfassung weiterleiten
            if (has(Roles.Zeiterfassung)) {
                return redirect('/zeiterfassung');
            }
            return invalidUserLogout();
        }
        // Falls der Benutzer kein Zeiterfassung User ist, müssen wir ihn aus der Zeiterfassung ausloggen...
        // (eingebettete Zeiterfassung in der ma-app fällt durch isApp('zeiterfassung'), kein extra Ausschluss nötig)
        if (isApp('zeiterfassung') && !has(Roles.Zeiterfassung)) {
            // Wenn der Benutzer neolohn Zugang hat, dann auf neolohn weiterleiten
            if (has(Roles.Sachbearbeiter) || has(Roles.DienstplanerMitBudget) || has(Roles.DienstplanerOhneBudget) || has(Roles.Zeitenpfleger)) {
                return redirect('/neolohn');
            }
            // Wenn der Benutzer Dienstplan Zugang hat, dann auf Dienstplan weiterleiten
            if (has(Roles.Dienstplaner)) {
                return redirect('/dienstplan');
            }
            return invalidUserLogout();
        }
    }

    async function invalidUserLogout() {
        clearTokenRefreshTimer();
        setTimeout(() => logout(), 10 * 1000);
        await SystemDialogService.instance.displayAsync('invalid-user-dialog');
        logout();
    }

    // wir forcieren das Update Token Event mit unseren eigenen Timer.
    function updateToken() {
        if (!keycloak.tokenParsed) {
            console.warn('[Auth] Missing tokenParsed during refresh attempt');
            return updateTokenTimeout();
        }
        // Falls offline, nochmal versuchen wenn wieder online
        if (!navigator.onLine) {
            console.warn('[Auth] Offline — skipped refresh');
            return updateTokenTimeout();
        }
        // if expires in less than 60 seconds, refresh
        keycloak.updateToken(60).then((wasUpdated) => {
            // Extract token info
            if (wasUpdated) {
                cookieService.setCookie('access_token', keycloak.token);
                console.log('[Auth] Token refresh OK');
            } else {
                // wasUpdated = false -> kein Refresh nötig, onAuthRefreshSuccess feuert nicht, also hier neu planen
                scheduleNextRefresh();
            }
        }).catch((err) => handleRefreshFailure(err));
    }

    /**
     * Behandelt fehlgeschlagene Token Refreshes. Nur wenn das aktuelle Token wirklich
     * abgelaufen ist, loggen wir aus - sonst war es ein transienter Fehler (z.B. kurzer
     * Netzwerkausfall, 5xx vom Keycloak, CORS) und wir versuchen es erneut.
     */
    async function handleRefreshFailure(err) {
        // Falls offline, nochmal versuchen wenn wieder online
        if (!navigator.onLine) {
            console.warn('[Auth] Offline — skipped refresh');
            return updateTokenTimeout();
        }
        // Wenn keycloak Token/Refresh-Token noch hat UND das Token noch gültig ist -> transienter Fehler, erneut versuchen.
        // (Reihenfolge wichtig: isTokenExpired() wirft "Not authenticated", wenn keycloak die Tokens schon verworfen hat,
        //  z.B. nach 400 invalid_grant / serverseitig beendeter Session.)
        if (keycloak.token && keycloak.refreshToken && !keycloak.isTokenExpired()) {
            console.warn('[Auth] Token refresh failed but token still valid, retrying...', err);
            return updateTokenTimeout(5000);
        }
        console.error('[Auth] Token refresh failed, logging out...', err);
        await sessionLogout();
    }

    function updateTokenTimeout(timeout = 30000) {
        // restart timer if existing
        if (window.myVars.updateTokenTimeout) {
            clearTimeout(window.myVars.updateTokenTimeout);
        }
        window.myVars.updateTokenTimeout = setTimeout(() => {
            updateToken();
        }, timeout - 2 * 1000);
    }

    // Plant den nächsten Refresh: spätestens in 30s, oder früher, falls das Token vorher abläuft.
    function scheduleNextRefresh() {
        const timeMax = keycloak.tokenParsed.exp * 1000 - new Date().getTime();
        const timeMin = Math.min(timeMax, 30 * 1000);
        updateTokenTimeout(Math.round(timeMin));
    }

    // Stoppt den Refresh-Timer, damit er während eines offenen Logout-Dialogs nicht weiterfeuert
    // (und z.B. das gerade gelöschte access_token Cookie wieder neu setzt).
    function clearTokenRefreshTimer() {
        if (window.myVars.updateTokenTimeout) {
            clearTimeout(window.myVars.updateTokenTimeout);
            window.myVars.updateTokenTimeout = null;
        }
    }

    keycloak.onAuthRefreshSuccess = scheduleNextRefresh;

    keycloak.onAuthRefreshError = () => handleRefreshFailure();

    keycloak.onAuthError = async () => {
        await sessionLogout();
    };

    // Feuert, wenn keycloak die Tokens verwirft (clearToken) - z.B. wenn ein anderes Fenster sich ausloggt
    // und der Login-Status-Iframe / ein 400 beim Refresh die beendete SSO-Session erkennt.
    keycloak.onAuthLogout = () => {
        clearTokenRefreshTimer();
        sessionLogout();
    };

    keycloak.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: `${window.location.origin}/assets/silent-check-sso.html`,
    }).then((authenticated) => {
        if (!authenticated) {
            keycloak.login();
        }
    }).catch(() => {
        keycloak.login();
    });

    document.getElementById('logout-link').addEventListener('click', () => {
        cookieService.deleteCookie();
        storeLastVistiedOnLogout();
        logout();
    });

    // für die ma-app und zeiterfassung brauchen wir keinen idle timeout...
    if (!isApp('ma-app') && !isApp('zeiterfassung')) {
        // 30 min idle timeout, über localStorage zwischen mehreren Fenstern synchronisiert
        const timeoutLogout = 30 * 60 * 1000;
        const IDLE_ACTIVITY_KEY = 'neolohn_idle_last_activity';
        const logoutTimerHTML = document.getElementById('logout-timer');
        let timeoutIdle;
        let lastActivityWrite = 0;

        const getLastActivity = () => {
            const stored = parseInt(localStorage.getItem(IDLE_ACTIVITY_KEY), 10);
            return Number.isNaN(stored) ? Date.now() : stored;
        };

        // Plant den Idle-Logout anhand der zuletzt (fensterübergreifend) registrierten Aktivität
        const scheduleIdleLogout = () => {
            clearTimeout(timeoutIdle);
            const remaining = timeoutLogout - (Date.now() - getLastActivity());
            if (remaining <= 0) {
                idleLogout();
                return;
            }
            timeoutIdle = setTimeout(scheduleIdleLogout, remaining);
        };

        // Aktivität (gedrosselt) in localStorage schreiben -> löst das storage-Event in den anderen Fenstern aus
        const registerActivity = () => {
            const now = Date.now();
            if (now - lastActivityWrite < 1000) {
                return;
            }
            lastActivityWrite = now;
            localStorage.setItem(IDLE_ACTIVITY_KEY, now.toString());
            scheduleIdleLogout();
        };

        // Aktivität in diesem Fenster registrieren
        ['click', 'mouseover', 'keyup'].forEach((event) => {
            document.body.addEventListener(event, registerActivity);
        });
        // Aktivität aus anderen Fenstern übernehmen
        window.addEventListener('storage', (event) => {
            if (event.key === IDLE_ACTIVITY_KEY) {
                scheduleIdleLogout();
            }
        });
        // Timer-Anzeige aktualisieren (anhand der fensterübergreifenden Aktivität)
        setInterval(() => {
            const remaining = timeoutLogout - (Date.now() - getLastActivity());
            // Wenn nur noch 5 Minuten übrig sind, zeigen wir den Timer an
            if (remaining <= 5 * 60 * 1000) {
                document.getElementById('logout-logout').style.display = 'none';
                document.getElementById('logout-timer-div').style.display = 'block';
                document.getElementById('logout-timer-div').parentNode.style.width = '80px';
                logoutTimerHTML.innerText = dayjs.duration(Math.max(remaining, 0), 'milliseconds').format('mm:ss');
                // Wenn nur noch 1 Minute übrig ist, zeigen wir den Timer in rot an.
                if (remaining <= 1 * 60 * 1000) {
                    logoutTimerHTML.classList.add('logout-last-minute');
                } else {
                    logoutTimerHTML.classList.remove('logout-last-minute');
                }
            } else {
                document.getElementById('logout-timer-div').style.display = 'none';
                document.getElementById('logout-logout').style.display = 'inline-block';
                document.getElementById('logout-timer-div').parentNode.style.width = '40px';
            }
        }, 500);

        // Seitenaufruf zählt als Aktivität -> Idle-Uhr starten bzw. fensterübergreifend zurücksetzen
        registerActivity();
    }

    /**
     * Beim Idle Logout, haben wir immer noch ein gültiges Token, das wir so einfach nicht invalidieren können,
     * deshalb wird der User direkt ausgeloggt.
     */
    async function idleLogout() {
        cookieService.deleteCookie();
        storeLastVistiedOnLogout();
        logout();
    }

    /**
     * Session Timeout, User wird benachrichtigt, warum er ausgeloggt wird.
     */
    async function sessionLogout() {
        // verhindert doppeltes Ausloggen, falls catch und onAuthRefreshError gleichzeitig feuern
        if (window.myVars.sessionLoggingOut) {
            return;
        }
        window.myVars.sessionLoggingOut = true;
        clearTokenRefreshTimer();
        keycloak.onAuthRefreshError = null;
        keycloak.onAuthRefreshSuccess = null;
        keycloak.onAuthError = null;
        keycloak.onAuthSuccess = null;
        cookieService.deleteCookie();
        // Der Client soll sich spätestens nach einer halben Stunde ganz abmelden, falls der User den Button nicht klickt.
        setTimeout(() => logout(), 30 * 60 * 1000);
        await SystemDialogService.byID('app-timeout-dialog', 11000).displayAsync('token-timeout-dialog', { closeOnBackdrop: false });
        logout();
    }

    /**
     * Blendet den AGB Dialog inklusive link zur AGB PDF ein, muss akzeptiert werden, sonst logout.
     * @param {*} tokenParsed 
     * @returns 
     */
    async function checkNeolohnAGB(tokenParsed) {
        try {
            // erstmal nur auf neolohn
            if (!window.location.href.split('/').includes('neolohn')) {
                return;
            }
            // falls AGB schon akzeptiert, kein Dialog anzeigen.
            if (new Date(tokenParsed?.AGB) >= new Date(2024, 2, 6)) {
                return;
            }
            const result = await SystemDialogService.byID('agb-dialog', '12000').displayAsync('neolohn-agb-dialog', { closeOnBackdrop: false });
            if (result.data.agb) {
                await fetch(`/neolohn/api/benutzer/${tokenParsed?.sub}/agb/confirm`, { method: 'POST' });
                return;
            }
            logout();
        } catch (error) {
            console.log(error);
            logout();
        }
    }
}

function acceptAGB() {
    SystemDialogService.byID('agb-dialog').confirm({
        agb: true
    });
}

function denyAGB() {
    SystemDialogService.byID('agb-dialog').confirm({
        agb: false
    });
}

export { initKeycloak };
